import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Stack, Tabs, Tab, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, TextField, FormControl, InputLabel, Select,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Divider, Card, CardContent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from '../api/axiosInstance';
import {
  fetchAuthorizedPayers,
  updateAuthorizedPayer,
  deleteAuthorizedPayer,
} from '../slices/authorizedPayersSlice';
import { useToast } from '../context/ToastContext';

const MotionPaper = motion(Paper);

const FONT = 'Cairo, Tajawal, sans-serif';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const PHONE_CODE_OPTIONS = [
  { value: '972', label: '+972' },
  { value: '970', label: '+970' },
];

const formatCurrency = (val) =>
  Number(val || 0).toLocaleString('en-US') + ' ₪';

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-GB');
};

const AuthorizedPayerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const { items: allPayers = [], loading: payersLoading } = useSelector(
    (state) => state.authorizedPayers || {}
  );

  const payer = allPayers.find((p) => p.id === id);

  const [activeTab, setActiveTab] = useState(0);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone_number: '', phone_code: '970' });

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Date filters for payments
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Store purchases tab
  const [storePurchases, setStorePurchases] = useState([]);
  const [storePurchasesTotal, setStorePurchasesTotal] = useState(0);
  const [storePurchasesLoading, setStorePurchasesLoading] = useState(false);
  const [spStartDate, setSpStartDate] = useState('');
  const [spEndDate, setSpEndDate] = useState('');

  useEffect(() => {
    if (!allPayers.length) {
      dispatch(fetchAuthorizedPayers());
    }
  }, [dispatch, allPayers.length]);

  useEffect(() => {
    if (payer) {
      setFormData({
        name: payer.name || payer.display_value || '',
        phone_number: payer.phone_number || '',
        phone_code: payer.phone_code || '970',
      });
    }
  }, [payer]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const url = `${API_BASE}/api/settings/authorized-payers/${id}/payments/?${params}`;
      const res = await axios.get(url);
      setPayments(res.data.results || []);
      setPaymentsTotal(res.data.total || 0);
    } catch {
      showToast('❌ تعذر تحميل سندات الصرف');
    } finally {
      setPaymentsLoading(false);
    }
  }, [id, startDate, endDate, showToast]);

  const loadStorePurchases = useCallback(async () => {
    setStorePurchasesLoading(true);
    try {
      const params = new URLSearchParams();
      if (spStartDate) params.set('start_date', spStartDate);
      if (spEndDate) params.set('end_date', spEndDate);
      const url = `${API_BASE}/api/settings/authorized-payers/${id}/store-purchases/?${params}`;
      const res = await axios.get(url);
      setStorePurchases(res.data.results || []);
      setStorePurchasesTotal(res.data.total || 0);
    } catch {
      showToast('❌ تعذر تحميل المشتريات');
    } finally {
      setStorePurchasesLoading(false);
    }
  }, [id, spStartDate, spEndDate, showToast]);

  useEffect(() => {
    if (activeTab === 1 || activeTab === 2) {
      loadPayments();
    }
    if (activeTab === 3) {
      loadStorePurchases();
    }
  }, [activeTab, loadPayments, loadStorePurchases]);

  const handleEdit = async () => {
    try {
      await dispatch(
        updateAuthorizedPayer({
          id,
          name: formData.name.trim(),
          display_value: formData.name.trim(),
          phone_number: formData.phone_number?.trim() || null,
          phone_code: formData.phone_code || '970',
        })
      ).unwrap();
      showToast('✅ تم تحديث بيانات المورد');
      setEditOpen(false);
    } catch {
      showToast('❌ حدث خطأ أثناء التحديث');
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteAuthorizedPayer(id)).unwrap();
      showToast('✅ تم حذف المورد');
      navigate('/authorized-payers');
    } catch {
      showToast('❌ حدث خطأ أثناء الحذف');
    }
    setDeleteOpen(false);
  };

  if (payersLoading && !payer) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '100vh', bgcolor: '#F8F9FF' }}
      >
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  if (!payer) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          direction: 'rtl',
          fontFamily: FONT,
        }}
      >
        <Typography sx={{ color: '#DC2626', fontWeight: 700, fontFamily: FONT }}>
          المورد غير موجود
        </Typography>
        <Button
          onClick={() => navigate('/authorized-payers')}
          startIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />}
          sx={{
            borderRadius: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            fontFamily: FONT,
            color: '#4338CA',
            '&:hover': { bgcolor: '#EEF2FF' },
          }}
        >
          العودة للقائمة
        </Button>
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />}
          onClick={() => navigate('/authorized-payers')}
          sx={{
            mb: 2,
            borderRadius: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            fontFamily: FONT,
            color: '#4338CA',
            '&:hover': { bgcolor: '#EEF2FF' },
          }}
        >
          العودة للموردون
        </Button>

        {/* Header card */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StoreIcon sx={{ color: '#fff', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: FONT }}
              >
                {payer.name || payer.display_value}
              </Typography>
              {payer.phone_number ? (
                <Stack useFlexGap direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                  <PhoneIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 18 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.85)', direction: 'ltr', fontFamily: FONT }}
                  >
                    ({payer.phone_code}) {payer.phone_number}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
                >
                  مورد
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack useFlexGap direction="row" spacing={1}>
            <Tooltip title="تعديل">
              <IconButton
                onClick={() => setEditOpen(true)}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="حذف">
              <IconButton
                onClick={() => setDeleteOpen(true)}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(220,38,38,0.85)' },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </MotionPaper>

        {/* Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            px: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontFamily: FONT,
                fontWeight: 700,
                textTransform: 'none',
                color: '#6B7280',
                minHeight: 56,
              },
              '& .Mui-selected': { color: '#4338CA !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4338CA', height: 3, borderRadius: 3 },
            }}
          >
            <Tab icon={<StoreIcon />} iconPosition="start" label="معلومات أساسية" />
            <Tab icon={<ReceiptIcon />} iconPosition="start" label="سندات الصرف" />
            <Tab icon={<BarChartIcon />} iconPosition="start" label="تقرير مالي" />
            <Tab icon={<ShoppingCartIcon />} iconPosition="start" label="المشتريات" />
          </Tabs>
        </Paper>

        {/* Tab 0: Basic Info */}
        {activeTab === 0 && (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#1E1B4B', mb: 2, fontFamily: FONT }}
            >
              المعلومات الأساسية
            </Typography>
            <Divider sx={{ mb: 3, borderColor: '#E5E7EB' }} />
            <Stack spacing={2.5}>
              <Stack useFlexGap direction="row" spacing={2}>
                <Typography sx={{ fontWeight: 700, color: '#6B7280', minWidth: 140, fontFamily: FONT }}>
                  اسم المورد:
                </Typography>
                <Typography sx={{ color: '#1E1B4B', fontWeight: 600, fontFamily: FONT }}>
                  {payer.name || payer.display_value}
                </Typography>
              </Stack>
              <Stack useFlexGap direction="row" spacing={2}>
                <Typography sx={{ fontWeight: 700, color: '#6B7280', minWidth: 140, fontFamily: FONT }}>
                  رقم الهاتف:
                </Typography>
                {payer.phone_number ? (
                  <Typography sx={{ direction: 'ltr', color: '#1E1B4B', fontWeight: 600, fontFamily: FONT }}>
                    ({payer.phone_code}) {payer.phone_number}
                  </Typography>
                ) : (
                  <Typography sx={{ color: '#6B7280', fontStyle: 'italic', fontFamily: FONT }}>
                    لا يوجد رقم
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  fontFamily: FONT,
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
              >
                تعديل المعلومات
              </Button>
            </Box>
          </MotionPaper>
        )}

        {/* Tab 1: Payments */}
        {activeTab === 1 && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            {/* Filters */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 2.5,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  type="date"
                  label="من تاريخ"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: FONT }}
                />
                <TextField
                  type="date"
                  label="إلى تاريخ"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: FONT }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={loadPayments}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    py: 0.9,
                    boxShadow: 'none',
                    fontFamily: FONT,
                    bgcolor: '#4338CA',
                    '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  }}
                >
                  بحث
                </Button>
                {(startDate || endDate) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                    }}
                    sx={{
                      borderRadius: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontFamily: FONT,
                      color: '#6B7280',
                      '&:hover': { bgcolor: '#F1F5F9' },
                    }}
                  >
                    مسح الفلتر
                  </Button>
                )}
              </Stack>
            </Paper>

            {/* Summary chip */}
            {!paymentsLoading && (
              <Stack useFlexGap direction="row" spacing={1.5} mb={2.5}>
                <Chip
                  label={`عدد السندات: ${payments.length}`}
                  sx={{
                    bgcolor: '#E0F2FE',
                    color: '#0284C7',
                    fontWeight: 700,
                    fontFamily: FONT,
                  }}
                />
                <Chip
                  label={`الإجمالي: ${formatCurrency(paymentsTotal)}`}
                  sx={{
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    fontWeight: 700,
                    fontFamily: FONT,
                  }}
                />
              </Stack>
            )}

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>رقم السند</TableCell>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>التاريخ</TableCell>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>المبلغ</TableCell>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>طريقة الدفع</TableCell>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>السبب</TableCell>
                      <TableCell align="center" sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>
                        إجراءات
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, borderColor: '#E5E7EB' }}>
                          <CircularProgress size={28} sx={{ color: '#4338CA' }} />
                        </TableCell>
                      </TableRow>
                    ) : payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#6B7280', fontFamily: FONT, borderColor: '#E5E7EB' }}>
                          لا توجد سندات صرف لهذا المورد
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment, idx) => (
                        <TableRow
                          key={payment.id}
                          hover
                          sx={{
                            bgcolor: idx % 2 === 0 ? '#fff' : '#F8F9FF',
                            '&:hover': { bgcolor: '#F8F9FF' },
                            '& .MuiTableCell-root': { borderColor: '#E5E7EB', fontFamily: FONT },
                          }}
                        >
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}>#{payment.number || '-'}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#6B7280' }}>{formatDate(payment.date)}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, color: '#DC2626', fontFamily: FONT }}>
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#1E1B4B' }}>{payment.payment_type || '-'}</TableCell>
                          <TableCell sx={{ color: '#6B7280' }}>{payment.reason || '-'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="عرض السند">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/finance/view/${payment.id}`)}
                                sx={{
                                  color: '#4338CA',
                                  '&:hover': { bgcolor: '#EEF2FF' },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* Tab 2: Financial Report */}
        {activeTab === 2 && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            {paymentsLoading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: '#4338CA' }} />
              </Box>
            ) : (
              <Stack spacing={3}>
                {/* Summary cards */}
                <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      borderRadius: 3,
                      color: '#fff',
                      background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ opacity: 0.85, mb: 1, fontFamily: FONT }}>
                        إجمالي المدفوعات
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: FONT }}>
                        {formatCurrency(paymentsTotal)}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      borderRadius: 3,
                      bgcolor: '#fff',
                      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" sx={{ color: '#6B7280', mb: 1, fontFamily: FONT }}>
                        عدد سندات الصرف
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#4338CA', fontFamily: FONT }}>
                        {payments.length}
                      </Typography>
                    </CardContent>
                  </Card>
                  {payments.length > 0 && (
                    <Card
                      elevation={0}
                      sx={{
                        flex: 1,
                        borderRadius: 3,
                        bgcolor: '#fff',
                        boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <CardContent>
                        <Typography variant="body2" sx={{ color: '#6B7280', mb: 1, fontFamily: FONT }}>
                          متوسط السند
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: FONT }}>
                          {formatCurrency(paymentsTotal / payments.length)}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Stack>

                {/* Payment type breakdown */}
                {payments.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, sm: 3 },
                      borderRadius: 3,
                      bgcolor: '#fff',
                      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E1B4B', mb: 2, fontFamily: FONT }}>
                      توزيع طرق الدفع
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: '#E5E7EB' }} />
                    {Object.entries(
                      payments.reduce((acc, p) => {
                        const type = p.payment_type || 'غير محدد';
                        acc[type] = (acc[type] || 0) + Number(p.amount);
                        return acc;
                      }, {})
                    ).map(([type, total]) => (
                      <Stack
                        key={type}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1.25}
                        sx={{ borderBottom: '1px solid #E5E7EB' }}
                      >
                        <Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>{type}</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#4338CA', fontFamily: FONT }}>{formatCurrency(total)}</Typography>
                      </Stack>
                    ))}
                  </Paper>
                )}
              </Stack>
            )}
          </Box>
        )}

        {/* Tab 3: Store Purchases */}
        {activeTab === 3 && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <Paper
              elevation={0}
              sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5, borderRadius: 3, bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB' }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField type="date" label="من تاريخ" value={spStartDate} onChange={e => setSpStartDate(e.target.value)}
                  size="small" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: FONT }} />
                <TextField type="date" label="إلى تاريخ" value={spEndDate} onChange={e => setSpEndDate(e.target.value)}
                  size="small" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, fontFamily: FONT }} />
                <Button variant="contained" size="small" onClick={loadStorePurchases}
                  sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', px: 2.5, py: 0.9, boxShadow: 'none', fontFamily: FONT, bgcolor: '#4338CA', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>
                  بحث
                </Button>
                {(spStartDate || spEndDate) && (
                  <Button size="small" onClick={() => { setSpStartDate(''); setSpEndDate(''); }}
                    sx={{ borderRadius: 2.5, fontWeight: 700, textTransform: 'none', fontFamily: FONT, color: '#6B7280', '&:hover': { bgcolor: '#F1F5F9' } }}>
                    مسح الفلتر
                  </Button>
                )}
              </Stack>
            </Paper>

            {!storePurchasesLoading && (
              <Stack useFlexGap direction="row" spacing={1.5} mb={2.5}>
                <Chip label={`عدد المشتريات: ${storePurchases.length}`}
                  sx={{ bgcolor: '#E0F2FE', color: '#0284C7', fontWeight: 700, fontFamily: FONT }} />
                <Chip label={`الإجمالي: ${Number(storePurchasesTotal).toLocaleString('en-US')} ₪`}
                  sx={{ bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 700, fontFamily: FONT }} />
              </Stack>
            )}

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(67,56,202,0.07)', border: '1px solid #E5E7EB' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>التاريخ</TableCell>
                      <TableCell sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>النوع</TableCell>
                      <TableCell align="right" sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>الكمية</TableCell>
                      <TableCell align="right" sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>سعر الوحدة</TableCell>
                      <TableCell align="right" sx={{ color: '#1E1B4B', fontWeight: 700, fontFamily: FONT, borderColor: '#E5E7EB' }}>الإجمالي</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storePurchasesLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, borderColor: '#E5E7EB' }}>
                          <CircularProgress size={28} sx={{ color: '#4338CA' }} />
                        </TableCell>
                      </TableRow>
                    ) : storePurchases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#6B7280', fontFamily: FONT, borderColor: '#E5E7EB' }}>
                          لا توجد مشتريات مرتبطة بهذا المورد
                        </TableCell>
                      </TableRow>
                    ) : (
                      storePurchases.map((p, idx) => (
                        <TableRow key={p.id} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#F8F9FF', '&:hover': { bgcolor: '#F8F9FF' }, '& .MuiTableCell-root': { borderColor: '#E5E7EB', fontFamily: FONT } }}>
                          <TableCell sx={{ color: '#6B7280' }}>{formatDate(p.purchase_date)}</TableCell>
                          <TableCell>
                            <Chip label={p.item_name || '—'} size="small" variant="outlined"
                              sx={{ fontFamily: FONT, fontWeight: 600, borderColor: '#FDE68A', color: '#D97706' }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#1E1B4B' }}>{p.quantity}</TableCell>
                          <TableCell align="right" sx={{ color: '#1E1B4B' }}>{Number(p.unit_price).toFixed(2)} ₪</TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 700, color: '#DC2626', fontFamily: FONT }}>
                              {Number(p.total_amount || p.quantity * p.unit_price).toFixed(2)} ₪
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* Edit Dialog */}
        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, direction: 'rtl', fontFamily: FONT } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}>
            تعديل بيانات المورد
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="اسم المورد"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Stack useFlexGap direction="row" spacing={0}>
                <FormControl sx={{ width: '35%' }}>
                  <InputLabel>الرمز</InputLabel>
                  <Select
                    value={formData.phone_code}
                    onChange={(e) => setFormData((p) => ({ ...p, phone_code: e.target.value }))}
                    label="الرمز"
                    sx={{ borderTopRightRadius: 8, borderBottomRightRadius: 8, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    {PHONE_CODE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="رقم الهاتف (اختياري)"
                  value={formData.phone_number}
                  onChange={(e) => setFormData((p) => ({ ...p, phone_number: e.target.value }))}
                  sx={{
                    width: '65%',
                    '& .MuiOutlinedInput-root': {
                      borderTopLeftRadius: 8,
                      borderBottomLeftRadius: 8,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setEditOpen(false)}
              sx={{ fontWeight: 700, textTransform: 'none', fontFamily: FONT, color: '#6B7280', borderRadius: 2.5 }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              onClick={handleEdit}
              disabled={!formData.name.trim()}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                boxShadow: 'none',
                fontFamily: FONT,
                bgcolor: '#4338CA',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              حفظ التعديلات
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          PaperProps={{ sx: { borderRadius: 3, direction: 'rtl', fontFamily: FONT } }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}>
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#1E1B4B', fontFamily: FONT }}>
              هل أنت متأكد من حذف المورد{' '}
              <strong>{payer.name || payer.display_value}</strong>؟ لن يمكن التراجع عن هذا الإجراء.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteOpen(false)}
              sx={{ fontWeight: 700, textTransform: 'none', fontFamily: FONT, color: '#6B7280', borderRadius: 2.5 }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                boxShadow: 'none',
                fontFamily: FONT,
                bgcolor: '#DC2626',
                '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
              }}
            >
              حذف
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AuthorizedPayerDetailPage;
